import {ChangeDetectorRef, Component, OnDestroy, OnInit, Optional} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {moveItemInArray} from '@angular/cdk/drag-drop';
import {FormControl, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import {DataManipulationService} from '../../services/data-manipulation.service';
import {ModalService} from '../../services/modal.service';
import {GenericConfirmModalComponent} from '../modals/generic-confirm-modal/generic-confirm-modal.component';
import {extractApiErrorMessage, OrganizeTeamsDataService} from './organize-teams-data.service';
import {
  LoadedTeam,
  MembersPage,
  OrganizeDropEvent,
  OrganizeMember,
  OrganizeTeamView,
  RemoveMemberEvent,
  SaveChange,
  SaveResult,
  SourceTeam,
  TeamAssignment,
  TeamLoadOutcome,
  TeamLoadState,
  TeamRoleKind,
  TeamRoles,
  TeamType,
  WorkspaceMemberRow
} from './organize-teams.models';

const SEARCH_DEBOUNCE_MS = 400;
const COPIED_FEEDBACK_MS = 1000;
const MIN_MEMBERS_FOR_FEEDBACK = 4;
const WORKSPACE_LIST_ID = 'workspace-members';
const LEADERS_LIST_ID = 'leaders';
const MEMBERS_LIST_ID = 'members';

interface SearchOutcome {
  term: string;
  members: OrganizeMember[] | null;
}

@Component({
  selector: 'app-organize-teams',
  templateUrl: './organize-teams.component.html',
  styleUrls: ['./organize-teams.component.scss'],
  providers: [OrganizeTeamsDataService]
})
export class OrganizeTeamsComponent implements OnInit, OnDestroy {
  teams: OrganizeTeamView[] = [];
  selectedTeam: OrganizeTeamView | null = null;
  workspaceRows: WorkspaceMemberRow[] = [];
  leaders: OrganizeMember[] = [];
  teamMembers: OrganizeMember[] = [];
  allChecked = false;
  searchValue = '';
  searching = false;
  showInvite = false;
  loadingMembers = false;
  initialLoading = false;
  saving = false;
  canSave = false;
  hasPreviousTeam = false;
  hasNextTeam = false;
  showMembersWarning = false;
  inviteLinkLoading = false;
  invitationLinkCopied = false;

  private sourceTeams: SourceTeam[] = [];
  private roles: TeamRoles | null = null;
  private readonly membersById = new Map<string, OrganizeMember>();
  // Display order of the workspace list: loaded pages first, then people only known from team lists.
  private memberOrder: string[] = [];
  private readonly assignments = new Map<string, Map<string, TeamAssignment>>();
  private readonly teamLoadStates = new Map<string, TeamLoadState>();
  private readonly teamTypes = new Map<string, TeamType | null>();
  private readonly checkedMembers = new Set<string>();
  // null when no search is active; otherwise the uuids returned by the search, in order.
  private searchResultUuids: string[] | null = null;
  // The term searchResultUuids belongs to, so the invite option never shows for outdated results.
  private searchedTerm = '';
  private membersPage = 0;
  private membersPageCount = 0;
  private readonly inviteLinks = new Map<string, string>();
  private readonly inviteLinkRequests = new Set<string>();
  private readonly emailCheck = new FormControl('', [Validators.required, Validators.email]);
  private readonly searchTerm$ = new Subject<string>();
  private readonly destroyed$ = new Subject<void>();
  private loadSubscription: Subscription | null = null;
  private copiedTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(private dataService: OrganizeTeamsDataService,
              private data: DataManipulationService,
              private modalService: ModalService,
              private translate: TranslateService,
              private cdr: ChangeDetectorRef,
              @Optional() private dialogRef: MatDialogRef<unknown> | null) {
  }

  ngOnInit(): void {
    this.data.gotOrganizeTeamsSubs.pipe(
      filter(Boolean),
      takeUntil(this.destroyed$)
    ).subscribe(() => this.loadAll());

    this.searchTerm$.pipe(
      map(term => term.trim()),
      debounceTime(SEARCH_DEBOUNCE_MS),
      distinctUntilChanged(),
      tap(term => {
        this.searching = term.length > 0;
        this.refreshView();
      }),
      switchMap(term => this.search(term)),
      takeUntil(this.destroyed$)
    ).subscribe(outcome => this.applySearch(outcome));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.loadSubscription?.unsubscribe();
    if (this.copiedTimeout) {
      clearTimeout(this.copiedTimeout);
    }
  }

  // ---- Team selection ----

  onSelectTeam(teamUuid: string): void {
    const team = this.teams.find(item => item.uuid === teamUuid);
    if (!team) {
      return;
    }
    this.selectedTeam = team;
    this.ensureInviteLink(teamUuid, false);
    this.refreshView();
  }

  onSelectSiblingTeam(offset: number): void {
    const index = this.selectedTeam ? this.teams.indexOf(this.selectedTeam) : -1;
    const sibling = this.teams[index + offset];
    if (sibling) {
      this.onSelectTeam(sibling.uuid);
    }
  }

  onRetryTeam(teamUuid: string): void {
    const team = this.sourceTeams.find(item => item.uuid === teamUuid);
    if (!team || this.teamLoadStates.get(teamUuid) === 'pending') {
      return;
    }
    this.teamLoadStates.set(teamUuid, 'pending');
    this.refreshView();
    this.dataService.loadTeam(team).pipe(
      takeUntil(this.destroyed$)
    ).subscribe({
      next: loaded => {
        this.applyTeam(loaded);
        this.refreshView();
      },
      error: () => {
        this.teamLoadStates.set(teamUuid, 'error');
        this.refreshView();
      }
    });
  }

  // ---- Workspace members list ----

  onSearchChange(term: string): void {
    this.searchValue = term;
    if (!term.trim()) {
      this.searchResultUuids = null;
      this.searchedTerm = '';
    }
    this.searchTerm$.next(term);
    this.refreshView();
  }

  onToggleMember(userUuid: string): void {
    if (this.checkedMembers.has(userUuid)) {
      this.checkedMembers.delete(userUuid);
    } else {
      this.checkedMembers.add(userUuid);
    }
    this.refreshView();
  }

  onToggleAll(checked: boolean): void {
    this.workspaceRows.forEach(row => {
      if (checked) {
        this.checkedMembers.add(row.member.userUuid);
      } else {
        this.checkedMembers.delete(row.member.userUuid);
      }
    });
    this.refreshView();
  }

  onLoadMore(): void {
    const searchActive = this.searchValue.trim().length > 0;
    if (this.loadingMembers || searchActive || this.membersPage >= this.membersPageCount) {
      return;
    }
    this.loadingMembers = true;
    this.dataService.loadMembersPage(this.membersPage + 1).pipe(
      finalize(() => {
        this.loadingMembers = false;
        this.refreshView();
      }),
      takeUntil(this.destroyed$)
    ).subscribe({
      next: page => {
        this.applyMembersPage(page);
        this.refreshView();
      },
      error: () => this.data.showAlertError(this.translate.instant('organizeteam_load_error'))
    });
  }

  onInvite(): void {
    const email = this.searchValue.replace(/\s/g, '');
    this.searchValue = email;
    this.emailCheck.setValue(email);
    if (!this.emailCheck.valid) {
      this.data.showAlertError(this.translate.instant('organizeteam_invalid_email', {email}));
      return;
    }
    this.data.showLoading();
    this.dataService.inviteToWorkspace(email, this.data.roleWorkspaceMember).pipe(
      finalize(() => this.data.hideLoading()),
      takeUntil(this.destroyed$)
    ).subscribe({
      next: member => {
        if (member) {
          this.rememberMember(member, true);
          this.memberOrder = [member.userUuid, ...this.memberOrder.filter(uuid => uuid !== member.userUuid)];
        }
        this.clearSearch();
        this.data.getWorkspaceMembers(1);
        this.refreshView();
      },
      error: () => this.data.showAlertError(this.translate.instant('organizeteam_invite_error'))
    });
  }

  // ---- Drag and drop ----

  onDrop(event: OrganizeDropEvent): void {
    const team = this.selectedTeam;
    const teamMap = team ? this.assignments.get(team.uuid) : undefined;
    if (!team || !teamMap || team.loadState !== 'loaded') {
      return;
    }
    const sourceId = event.sourceListId;
    const targetId = event.targetListId;
    const dragged = event.member;

    if (sourceId === targetId) {
      this.reorder(targetId, teamMap, event.previousIndex, event.currentIndex);
    } else if (targetId === LEADERS_LIST_ID || targetId === MEMBERS_LIST_ID) {
      const role: TeamRoleKind = targetId === LEADERS_LIST_ID ? 'leader' : 'member';
      // Checked workspace members travel with the dragged one, but only when dragging from the workspace list.
      const moving = [dragged.userUuid];
      if (sourceId === WORKSPACE_LIST_ID) {
        this.workspaceRows
          .filter(row => row.checked && row.member.userUuid !== dragged.userUuid)
          .forEach(row => moving.push(row.member.userUuid));
      }
      moving.forEach(userUuid => {
        this.assign(teamMap, userUuid, role);
        this.checkedMembers.delete(userUuid);
      });
      this.placeInRole(teamMap, role, dragged.userUuid, event.currentIndex);
    }
    this.refreshView();
  }

  // ---- Removing members ----

  onRemove(event: RemoveMemberEvent): void {
    const team = this.selectedTeam;
    const teamMap = team ? this.assignments.get(team.uuid) : undefined;
    const assignment = teamMap?.get(event.userUuid);
    const member = this.membersById.get(event.userUuid);
    if (!team || !teamMap || !assignment || !member) {
      return;
    }
    const membershipUuid = assignment.membershipUuid;
    if (!membershipUuid) {
      teamMap.delete(event.userUuid);
      this.refreshView();
      return;
    }
    this.modalService.openModal(GenericConfirmModalComponent,
      {
        title: {
          key: 'team_confirmation_needed'
        },
        description: {
          key: 'to_go_to_teams_delete_member_modal_subtitle',
          params: {
            name: member.displayName,
            teamName: team.name
          }
        },
        buttons: [
          {labelKey: 'yes', value: 'team', className: 'delete-from-team-btn'},
          {labelKey: 'remove_them_from_workspace', value: 'workspace', className: 'delete-from-workspace-btn'},
          {labelKey: 'cancel_button', value: false, className: 'no-btn'}
        ]
      },
      {panelClass: 'generic-modal'}
    )
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: unknown) => {
        if (result === 'team') {
          this.removeFromTeam(team.uuid, member.userUuid, membershipUuid);
        } else if (result === 'workspace') {
          this.removeFromWorkspace(member.userUuid);
        }
      });
  }

  // ---- Footer ----

  onCopyInvitationLink(): void {
    const team = this.selectedTeam;
    if (!team) {
      return;
    }
    const link = this.inviteLinks.get(team.uuid);
    if (!link) {
      this.ensureInviteLink(team.uuid, true);
      return;
    }
    // The link is already cached, so the clipboard write still happens inside the click gesture.
    navigator.clipboard.writeText(link).then(
      () => this.showCopiedFeedback(),
      () => this.data.showAlertError(this.translate.instant('organizeteam_copy_link_error'))
    );
  }

  onDone(): void {
    const roles = this.roles;
    if (!this.canSave || !roles) {
      return;
    }
    const changes = this.collectChanges();
    if (changes.length === 0) {
      this.dialogRef?.close();
      return;
    }
    this.saving = true;
    this.refreshView();
    this.data.showLoading();
    this.dataService.save(changes, roles).pipe(
      finalize(() => {
        this.saving = false;
        this.data.hideLoading();
        this.refreshView();
      }),
      takeUntil(this.destroyed$)
    ).subscribe(results => this.handleSaveResults(results));
  }

  // ---- Loading ----

  private loadAll(): void {
    this.loadSubscription?.unsubscribe();
    this.resetState(this.data.organizeTeams);
    const [firstTeam, ...otherTeams] = this.sourceTeams;
    if (!firstTeam) {
      return;
    }
    this.initialLoading = true;
    this.data.showLoading();
    this.loadSubscription = forkJoin({
      roles: this.dataService.loadRoles(),
      page: this.dataService.loadMembersPage(1),
      team: this.dataService.loadTeam(firstTeam)
    }).pipe(
      finalize(() => {
        this.initialLoading = false;
        this.data.hideLoading();
        this.refreshView();
      }),
      tap(({roles, page, team}) => {
        this.roles = roles;
        this.applyMembersPage(page);
        this.applyTeam(team);
        this.onSelectTeam(firstTeam.uuid);
      }),
      switchMap(() => this.dataService.loadTeams(otherTeams)),
      takeUntil(this.destroyed$)
    ).subscribe({
      next: outcome => this.applyTeamOutcome(outcome),
      error: () => {
        this.data.showAlertError(this.translate.instant('organizeteam_load_error'));
        this.dialogRef?.close();
      }
    });
  }

  private resetState(sourceTeams: SourceTeam[]): void {
    this.sourceTeams = sourceTeams;
    this.roles = null;
    this.membersById.clear();
    this.memberOrder = [];
    this.assignments.clear();
    this.teamLoadStates.clear();
    this.teamTypes.clear();
    this.checkedMembers.clear();
    this.searchResultUuids = null;
    this.searchedTerm = '';
    this.searchValue = '';
    this.membersPage = 0;
    this.membersPageCount = 0;
    this.selectedTeam = null;
    sourceTeams.forEach(team => this.teamLoadStates.set(team.uuid, 'pending'));
    // The views are kept stable (and updated in place) so the carousel doesn't re-render on every change.
    this.teams = sourceTeams.map(team => ({
      uuid: team.uuid,
      name: team.name,
      initial: team.name.charAt(0).toUpperCase(),
      teamType: null,
      leaderCount: 0,
      memberCount: 0,
      loadState: 'pending'
    }));
    this.refreshView();
  }

  private applyMembersPage(page: MembersPage): void {
    page.members.forEach(member => this.rememberMember(member, true));
    this.membersPage = page.page;
    this.membersPageCount = page.pageCount;
  }

  private applyTeamOutcome(outcome: TeamLoadOutcome): void {
    if (outcome.team) {
      this.applyTeam(outcome.team);
    } else {
      this.teamLoadStates.set(outcome.teamUuid, 'error');
    }
    this.refreshView();
  }

  private applyTeam(team: LoadedTeam): void {
    // Team-member rows have no reliable start-guide counters, so they never replace a workspace-users entry.
    team.members.forEach(member => this.rememberMember(member, false));
    const teamMap = new Map<string, TeamAssignment>();
    // Copied because the component edits its assignments in place.
    team.assignments.forEach(assignment => teamMap.set(assignment.userUuid, {...assignment}));
    this.assignments.set(team.teamUuid, teamMap);
    this.teamTypes.set(team.teamUuid, team.teamType);
    this.teamLoadStates.set(team.teamUuid, 'loaded');
  }

  private rememberMember(member: OrganizeMember, replaceExisting: boolean): void {
    if (!this.membersById.has(member.userUuid)) {
      this.memberOrder.push(member.userUuid);
    } else if (!replaceExisting) {
      return;
    }
    this.membersById.set(member.userUuid, member);
  }

  // ---- Search ----

  private search(term: string): Observable<SearchOutcome> {
    if (!term) {
      return of({term, members: []});
    }
    return this.dataService.searchMembers(term).pipe(
      map((members): SearchOutcome => ({term, members})),
      catchError(() => of<SearchOutcome>({term, members: null}))
    );
  }

  private applySearch(outcome: SearchOutcome): void {
    this.searching = false;
    this.searchedTerm = outcome.term;
    if (!outcome.term) {
      this.searchResultUuids = null;
    } else if (outcome.members === null) {
      this.data.showAlertError(this.translate.instant('organizeteam_load_error'));
    } else {
      // Search hits are not added to the display order: like before, they only stay visible while searching
      // (or once they are assigned to a team).
      outcome.members.forEach(member => this.membersById.set(member.userUuid, member));
      this.searchResultUuids = outcome.members.map(member => member.userUuid);
    }
    this.refreshView();
  }

  private clearSearch(): void {
    this.searchValue = '';
    this.searchResultUuids = null;
    this.searchedTerm = '';
    this.searchTerm$.next('');
  }

  // ---- Assignments ----

  private assign(teamMap: Map<string, TeamAssignment>, userUuid: string, role: TeamRoleKind): void {
    const existing = teamMap.get(userUuid);
    if (existing) {
      existing.role = role;
    } else {
      teamMap.set(userUuid, {userUuid, role, originalRole: null, membershipUuid: null});
    }
    if (!this.memberOrder.includes(userUuid)) {
      this.memberOrder.push(userUuid);
    }
  }

  private reorder(listId: string, teamMap: Map<string, TeamAssignment>, previousIndex: number, currentIndex: number): void {
    if (listId === WORKSPACE_LIST_ID) {
      const moved = this.workspaceRows[previousIndex]?.member.userUuid;
      const anchor = this.workspaceRows[currentIndex]?.member.userUuid;
      const order = this.searchResultUuids ?? this.memberOrder;
      if (moved && anchor && moved !== anchor) {
        moveItemInArray(order, order.indexOf(moved), order.indexOf(anchor));
      }
      return;
    }
    const role: TeamRoleKind = listId === LEADERS_LIST_ID ? 'leader' : 'member';
    const list = role === 'leader' ? this.leaders : this.teamMembers;
    const moved = list[previousIndex];
    if (moved) {
      this.placeInRole(teamMap, role, moved.userUuid, currentIndex);
    }
  }

  /** Moves the user to the given position among the team's assignments with that role. */
  private placeInRole(teamMap: Map<string, TeamAssignment>, role: TeamRoleKind, userUuid: string, index: number): void {
    const moved = teamMap.get(userUuid);
    if (!moved) {
      return;
    }
    const sameRole = Array.from(teamMap.values()).filter(item => item.role === role && item.userUuid !== userUuid);
    sameRole.splice(Math.min(index, sameRole.length), 0, moved);
    const others = Array.from(teamMap.values()).filter(item => item.role !== role);
    teamMap.clear();
    [...sameRole, ...others].forEach(item => teamMap.set(item.userUuid, item));
  }

  private removeFromTeam(teamUuid: string, userUuid: string, membershipUuid: string): void {
    this.data.showLoading();
    this.dataService.removeFromTeam(membershipUuid, teamUuid).pipe(
      finalize(() => this.data.hideLoading()),
      takeUntil(this.destroyed$)
    ).subscribe({
      next: () => {
        this.assignments.get(teamUuid)?.delete(userUuid);
        this.refreshView();
        this.data.showAlert(this.translate.instant('organizeteam_member_deleted'));
      },
      error: err => this.data.showAlertError(extractApiErrorMessage(err) ?? this.translate.instant('organizeteam_action_error'))
    });
  }

  private removeFromWorkspace(userUuid: string): void {
    this.data.showLoading();
    this.dataService.removeFromWorkspace(userUuid).pipe(
      finalize(() => this.data.hideLoading()),
      takeUntil(this.destroyed$)
    ).subscribe({
      next: () => {
        this.assignments.forEach(teamMap => teamMap.delete(userUuid));
        this.membersById.delete(userUuid);
        this.memberOrder = this.memberOrder.filter(uuid => uuid !== userUuid);
        this.searchResultUuids = this.searchResultUuids?.filter(uuid => uuid !== userUuid) ?? null;
        this.checkedMembers.delete(userUuid);
        this.data.getWorkspaceMembers(1);
        this.refreshView();
        this.data.showAlert(this.translate.instant('organizeteam_member_removed'));
      },
      error: err => this.data.showAlertError(extractApiErrorMessage(err) ?? this.translate.instant('organizeteam_action_error'))
    });
  }

  // ---- Saving ----

  private collectChanges(): SaveChange[] {
    const changes: SaveChange[] = [];
    this.assignments.forEach((teamMap, teamUuid) => teamMap.forEach(assignment => {
      if (assignment.originalRole === null) {
        changes.push({kind: 'add', teamUuid, userUuid: assignment.userUuid, role: assignment.role});
      } else if (assignment.role !== assignment.originalRole) {
        changes.push({kind: 'changeRole', teamUuid, userUuid: assignment.userUuid, role: assignment.role});
      }
    }));
    return changes;
  }

  private handleSaveResults(results: SaveResult[]): void {
    const failed = results.filter(result => !result.ok);
    if (failed.length === 0) {
      this.finishSave();
      return;
    }
    results.filter(result => result.ok).forEach(result => this.applySavedChange(result));
    const messages = new Set(failed.map(result => result.errorMessage).filter((message): message is string => !!message));
    this.data.showAlertError([this.translate.instant('organizeteam_save_error'), ...Array.from(messages)].join(' '));
    this.refreshView();
  }

  private applySavedChange(result: SaveResult): void {
    const {change} = result;
    const teamMap = this.assignments.get(change.teamUuid);
    const assignment = teamMap?.get(change.userUuid);
    if (!teamMap || !assignment) {
      return;
    }
    if (change.kind === 'changeRole') {
      assignment.originalRole = change.role;
    } else if (result.active && result.membershipUuid) {
      assignment.originalRole = change.role;
      assignment.membershipUuid = result.membershipUuid;
    } else {
      // New memberships start inactive and the team list only shows active ones, so the person goes
      // back to the workspace list as unassigned.
      teamMap.delete(change.userUuid);
    }
  }

  private finishSave(): void {
    if (this.data.openFromTeam) {
      this.data.addedMembersToTeam(true);
    } else {
      this.data.getAllTeams = false;
      this.data.getWorkspaceTeams();
    }
    this.data.getWorkspaceMembers(1);
    this.dialogRef?.close();
    this.data.goToTeamCreated();
  }

  // ---- Invite link ----

  private ensureInviteLink(teamUuid: string, userTriggered: boolean): void {
    if (this.inviteLinks.has(teamUuid) || this.inviteLinkRequests.has(teamUuid)) {
      return;
    }
    this.inviteLinkRequests.add(teamUuid);
    this.refreshView();
    this.dataService.getInviteLink(teamUuid).pipe(
      finalize(() => {
        this.inviteLinkRequests.delete(teamUuid);
        this.refreshView();
      }),
      takeUntil(this.destroyed$)
    ).subscribe({
      next: link => this.inviteLinks.set(teamUuid, link),
      error: () => {
        // A failed prefetch stays silent; the next click on "copy link" retries and reports the error.
        if (userTriggered) {
          this.data.showAlertError(this.translate.instant('organizeteam_copy_link_error'));
        }
      }
    });
  }

  private showCopiedFeedback(): void {
    this.invitationLinkCopied = true;
    this.refreshView();
    if (this.copiedTimeout) {
      clearTimeout(this.copiedTimeout);
    }
    this.copiedTimeout = setTimeout(() => {
      this.invitationLinkCopied = false;
      this.refreshView();
    }, COPIED_FEEDBACK_MS);
  }

  // ---- Derived view state ----

  private refreshView(): void {
    const assignedUsers = new Set<string>();
    this.assignments.forEach(teamMap => teamMap.forEach((_, userUuid) => assignedUsers.add(userUuid)));

    this.teams.forEach((view, index) => {
      const teamMap = this.assignments.get(view.uuid);
      const assignments = teamMap ? Array.from(teamMap.values()) : [];
      view.leaderCount = assignments.filter(item => item.role === 'leader').length;
      view.memberCount = assignments.filter(item => item.role === 'member').length;
      view.loadState = this.teamLoadStates.get(view.uuid) ?? 'pending';
      view.teamType = this.teamTypes.get(view.uuid) ?? null;
      const source = this.sourceTeams[index];
      if (view.loadState === 'loaded' && source) {
        // Other screens read these counters from the shared team objects.
        source.leaders = view.leaderCount;
        source.members = view.memberCount;
      }
    });

    const selectedMap = this.selectedTeam ? this.assignments.get(this.selectedTeam.uuid) : undefined;
    this.leaders = [];
    this.teamMembers = [];
    selectedMap?.forEach(assignment => {
      const member = this.membersById.get(assignment.userUuid);
      if (member) {
        (assignment.role === 'leader' ? this.leaders : this.teamMembers).push(member);
      }
    });

    this.workspaceRows = (this.searchResultUuids ?? this.memberOrder)
      .filter(userUuid => !selectedMap?.has(userUuid))
      .map(userUuid => this.membersById.get(userUuid))
      .filter((member): member is OrganizeMember => member !== undefined)
      .map(member => ({
        member,
        assigned: member.hasServerAssignment || assignedUsers.has(member.userUuid),
        checked: this.checkedMembers.has(member.userUuid)
      }));
    this.allChecked = this.workspaceRows.length > 0 && this.workspaceRows.every(row => row.checked);

    const term = this.searchValue.trim();
    const termIsInTeam = [...this.leaders, ...this.teamMembers]
      .some(member => member.email.toLowerCase() === term.toLowerCase());
    this.showInvite = this.searchResultUuids !== null && !this.searching && term.length > 0
      && term === this.searchedTerm && this.workspaceRows.length === 0 && !termIsInTeam;

    const selectedIndex = this.selectedTeam ? this.teams.indexOf(this.selectedTeam) : -1;
    this.hasPreviousTeam = selectedIndex > 0;
    this.hasNextTeam = selectedIndex >= 0 && selectedIndex < this.teams.length - 1;
    this.showMembersWarning = this.teamMembers.length > 0 && this.teamMembers.length < MIN_MEMBERS_FOR_FEEDBACK;
    this.inviteLinkLoading = !!this.selectedTeam && this.inviteLinkRequests.has(this.selectedTeam.uuid);
    const allTeamsSettled = this.teams.every(team => team.loadState !== 'pending');
    this.canSave = !this.saving && !this.initialLoading && allTeamsSettled && this.roles !== null;
    // The modal is hosted inside the OnPush ModalWrapperComponent, so async updates must mark the view
    // for check explicitly; otherwise they only appear after a DOM event inside the component.
    this.cdr.markForCheck();
  }
}
